import "./SearchItem.module.scss";

import {
    faMinusSquare,
    faPaperclip,
    faPlusSquare,
} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {boundMethod} from "autobind-decorator";
import {parseISO} from "date-fns";
import React from "react";
import {injectIntl, IntlShape} from "react-intl";
import {Link} from "react-router-dom";

import {ECheckStates} from "@toolbox/button-like/models";
import {
    IDocIdInfo,
    IItemSelectionState,
    ISearchDocumentModel,
    ISearchItem,
} from "./models";

import TristateCheckbox from "@toolbox/button-like/TristateCheckbox";
import Time from "@toolbox/design/Time";
import Highlighter from "@toolbox/display-blocks/Highlighter";
import ModuleIcon from "@toolbox/display-blocks/ModuleIcon";
import ACsvErrorIndicator from "./ACsvErrorIndicator";
import CentrifugeSamples from "./CentrifugeSamples";
import FractureSamples from "./FractureSamples";
import ProjectRecordIcon from "./ProjectRecordIcon";
import RecordingIndicator from "./RecordingIndicator";
import {isCalibration} from "./SearchResult";
import SpocSamples from "./spoc-samples/Index";

export function isSelected(nestedId: string, selection: IItemSelectionState) {
    if (!selection) {
        return false;
    }

    if (selection === true) {
        return true;
    }

    return selection.indexOf(nestedId) >= 0;
}

export function getSelection(
    items: {id: string; selected: boolean}[],
): IItemSelectionState {
    const selected = items.filter((x) => x.selected);
    switch (selected.length) {
        case 0:
            return null;

        case items.length:
            return true;

        default:
            return selected.map((x) => x.id);
    }
}

export interface ISearchItemProps {
    intl: IntlShape;
    item: ISearchItem;
    project: number;
    index: number;

    query: string;
    isExpanded: boolean;
    selection: IItemSelectionState;
    onSelected(
        id: IDocIdInfo,
        doc: ISearchDocumentModel,
        value: IItemSelectionState,
    ): void;
    onExpanding(isExpanded: boolean, index: number): void;
}

class SearchItem extends React.PureComponent<ISearchItemProps> {
    private get checkState() {
        const {selection} = this.props;
        if (!selection) {
            return ECheckStates.NotChecked;
        }

        if (selection === true) {
            return ECheckStates.Checked;
        }

        return ECheckStates.Indeterminate;
    }

    @boundMethod
    public toggleExpand(e: React.SyntheticEvent) {
        e.preventDefault();

        const {isExpanded, index, onExpanding} = this.props;
        onExpanding(!isExpanded, index);
    }

    @boundMethod
    public onSelfSelected(checked: boolean) {
        const {item, onSelected} = this.props;
        const value: IItemSelectionState = checked ? true : null;
        onSelected(item.id, item.doc, value);
    }

    @boundMethod
    public onNestedSelected(selection: IItemSelectionState) {
        const {item, onSelected} = this.props;
        onSelected(item.id, item.doc, selection);
    }

    public render() {
        const {intl, item, project, query} = this.props;
        const {doc, id} = item;
        const {
            aCsvError,
            completion,
            created,
            docId,
            hasAttachment,
            modified,
            name,
            nestedItems,
        } = doc;

        const containsAttachment =
            hasAttachment ||
            nestedItems?.some(
                (a) =>
                    a.hasAttachment ||
                    a.nestedItems?.some((b) => b.hasAttachment),
            );

        return (
            <React.Fragment>
                <td className="table-checkbox-padding">
                    <label>
                        <TristateCheckbox
                            suffixId={id.id}
                            checkboxClassNameOverwrite="checkbox-primary"
                            value={this.checkState}
                            onChange={this.onSelfSelected}
                        />
                    </label>
                </td>
                <td styleName="doc-icon">
                    <ModuleIcon icon={id.icon} />
                </td>

                {this.renderExpandButton()}

                <td styleName="doc-name">
                    <Link to={id.url}>
                        <Highlighter query={query} text={name} />
                        {this.renderTimestamp(doc)}
                    </Link>
                    {containsAttachment && (
                        <FontAwesomeIcon
                            className="ml-2 text-muted"
                            icon={faPaperclip}
                        />
                    )}
                    <ProjectRecordIcon docId={docId} project={project} />
                    <RecordingIndicator completion={parseISO(completion)} />
                    <ACsvErrorIndicator hasError={aCsvError} />
                    {this.renderNestedItems()}
                </td>
                <td styleName="row-with-icon">{id.type(intl)}</td>
                <td styleName="row-with-icon">
                    <Time value={parseISO(modified)} />
                </td>
                <td styleName="row-with-icon">
                    <Time value={parseISO(created)} />
                </td>
            </React.Fragment>
        );
    }

    private renderExpandButton() {
        const {item, isExpanded} = this.props;
        if (!item.doc.nestedItems?.length || isCalibration(item.doc)) {
            return <td />;
        }

        return (
            <td
                styleName="row-with-icon"
                id={"expand-" + item.id.id}
                onClick={this.toggleExpand}
            >
                <FontAwesomeIcon
                    className="primary-color"
                    icon={!isExpanded ? faPlusSquare : faMinusSquare}
                    fixedWidth={true}
                />
            </td>
        );
    }

    private renderNestedItems() {
        const {project, item, selection, isExpanded, query} = this.props;
        if (!isExpanded) {
            return null;
        }

        return (
            <ul className="list-unstyled">
                <FractureSamples
                    project={project}
                    selection={selection}
                    items={item.doc.nestedItems}
                    query={query}
                    onChange={this.onNestedSelected}
                />
                <CentrifugeSamples
                    project={project}
                    selection={selection}
                    items={item.doc.nestedItems}
                    query={query}
                    onChange={this.onNestedSelected}
                />
                <SpocSamples
                    project={project}
                    selection={selection}
                    items={item.doc.nestedItems}
                    query={query}
                    onChange={this.onNestedSelected}
                />
            </ul>
        );
    }

    private renderTimestamp(doc: ISearchDocumentModel) {
        if (isCalibration(doc)) {
            return (
                <span className="text-muted ml-1">
                    <Time value={parseISO(doc.created)} />
                </span>
            );
        }
    }
}

export default injectIntl(SearchItem);
